// Typed fetch wrapper rondom de backend API.
import type {
  UserPublic,
  Pricelist,
  PricelistItem,
  Ploeg,
  ProjectListItem,
  ProjectStatus,
  ParsedItem,
  ProjectMeta,
  MatchResult,
  Totals,
  AiUsage,
  CreateProjectRequest,
  ChargeLine,
  ActivityLogEntry,
  LearningData,
  ExtraRegel,
  GevelConfig,
  GevelKeuze,
  GevelVariant,
  ProjectKost,
  ToegerekendeRegel,
  WeergaveRij,
  TweePercentages,
  OpenPost,
  LogCategorie,
  WorkflowMelding,
  WorkflowStaat,
} from '@recotex/shared';

/** Ploeg zoals de API ze teruggeeft: met de prijslijst per type werken. */
export type PloegMetPrijslijsten = Ploeg & { gevelVariant: GevelVariant; dakHellendPricelistCode: string | null };

/** OA-percentage op projectniveau (SPEC_wijzigingen 2.4–2.5). */
export interface ProjectPercentage {
  sleutel: string;
  projectNummer: string;
  kost: ProjectKost;
  noemer: number | null;
  basis: 'gefactureerd' | 'offertewaarde' | null;
  pct: number | null;
  voorlopig: boolean;
  calculaties: Array<{
    id: string; projectNummer: string; klantNaam: string; ploegTeam: string; status: ProjectStatus;
    verstuurdOp: string | null; createdAt: string;
    subtotaal: number; meerwerk: number; extraWerk: number; aanrekening: number; totaal: number;
    versie: number; verstuurdeVersie: number | null; openBedrag: number;
  }>;
  vanElders: Array<ToegerekendeRegel & { calcPloegTeam: string }>;
  /** Twee percentages (27okt 3): op de offertewaarde en op het gefactureerde. */
  percentages: TweePercentages;
  referentie: Referentie | null;
}

/** Referentiebedragen per P-nummer uit Bouwflow (27okt 3). */
export interface Referentie {
  projectNummer: string;
  bouwflowProjectId: number | null;
  offertewaarde: number | null;
  gefactureerd: number | null;
  creditnotas: number | null;
  projectleider: string | null;
  opgehaaldOp: string | null;
  offertes: Array<{ id: number; nummer: string | null; datum: string | null; status: string; bedrag: number }>;
  facturen: Array<{ id: number; type: number | null; status: string; bedrag: number }>;
}

/** Rij in het blok "Prijsafwijkingen t.o.v. standaardprijzen" (27okt 2). */
export interface PrijsafwijkingRij {
  regelSleutel: string;
  soort: 'werkpost' | 'meerwerk';
  lijnId: string;
  post: string;
  eenheid: string;
  hoeveelheid: number;
  standaard: number;
  toegepast: number;
  verschilPerEenheid: number;
  verschilPct: number | null;
  impact: number;
  reden: string;
  door: string | null;
  op: string | null;
  gemeldOp: string | null;
  vanVoorHistoriek: boolean;
}

export interface VersieRij {
  versie: number;
  aanleiding: 'aangemaakt' | 'opgeslagen' | 'stand_bij_activatie' | 'vrijgave' | 'nieuwe_versie' | string;
  totaal: number;
  aangemaaktDoorNaam: string;
  aangemaaktOp: string;
  verstuurdOp: string | null;
}

export interface VrijgaveRij {
  id: string;
  versie: number;
  bedrag: number;
  vrijgegevenDoorNaam: string;
  vrijgegevenOp: string;
  verstuurdOp: string | null;
  posten: OpenPost[];
}

export interface TagRij { id: string; naam: string; bron: 'manueel' | 'automatisch' }
export interface TagLijstRij { id: string; naam: string; vast: boolean; automatisch: boolean; gebruik: number }
export interface NotitieRij {
  id: string; projectSleutel: string; tekst: string; auteurId: string | null; auteurNaam: string; persoon: string;
  aangemaaktOp: string; gewijzigdOp: string | null; vastgepind: boolean;
  /** Getagde collega's: kregen een mail met de notitie. */
  getagd?: Array<{ id: string; naam: string }>;
}
export interface Collega { id: string; naam: string }
export interface ProjectLogRij {
  id: string; createdAt: number; userId: string | null; userDisplay: string; userEmail: string; persoon: string;
  action: string; entityType: string; entityId: string; entityLabel: string; categorie: LogCategorie; reden: string;
  details: Record<string, unknown>;
}
/** 8.6: wat er met de upload naar Bouwflow gebeurde bij het versturen. */
export interface BouwflowUploadAntwoord { status: 'uit' | 'geen_koppeling' | 'al_geplaatst' | 'gelukt' | 'mislukt'; fout?: string }
export interface WorkflowInfo {
  start: string | null;
  laatstePoll: string | null;
  laatsteDagelijks: string | null;
  open: number;
  mailsAan: boolean;
  ontvangers: string[];
  ontvangersDagelijks: string[];
}
export interface RolRij { id: string; naam: string; niveau: number; magBevestigen: boolean; magVrijgeven: boolean; magBeheren: boolean }

export interface ExtraRegelFilters {
  soort?: '' | 'extra_werk' | 'aanrekenbare_kost';
  categorie?: string;
  ploegId?: string;
  oorspronkelijkePloegId?: string;
  toegerekend?: string;
  anderProject?: '' | '1';
  teltMee?: '' | 'ja' | 'nee';
}

export interface ExtraRegelOverzichtRij {
  id: string;
  soort: 'extra_werk' | 'aanrekenbare_kost';
  categorie: string;
  omschrijving: string;
  reden: string;
  bedrag: number;
  toegerekendProject: string;
  teltMeeInPercentage: boolean;
  bron: string;
  aangemaaktOp: string;
  calcId: string;
  calcProjectNummer: string;
  calcKlantNaam: string;
  calcStatus: ProjectStatus;
  calcVerstuurdOp: string | null;
  ploegTeam: string;
  oorspronkelijkePloegTeam: string | null;
}

export type ProjectUpdateBody = Partial<{
  klantNaam: string; klantAdres: string; adviseur: string; projectTitel: string; projectNummer: string; opmerkingen: string;
  ploegId: string; status: ProjectStatus;
  parsedItemsJson: string; matchedLinesJson: string; meerwerkLinesJson: string; skippedItemsJson: string;
  extraRegels: ExtraRegel[];
  gevel: GevelKeuze | null;
  offertewaarde: number | null;
  /** Versie waarop de bewerking gebaseerd is (conflictregel, 27okt 7). */
  basisVersie: number;
  /** Expliciet een nieuwe versie maken van een verstuurde calculatie. */
  nieuweVersie: boolean;
}>;

const BASE = '/api';

class ApiError extends Error {
  constructor(public status: number, public payload: unknown) {
    super(`API ${status}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body && !(init.body instanceof FormData) ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!resp.ok) {
    let payload: unknown = null;
    try { payload = await resp.json(); } catch { /* ignore */ }
    throw new ApiError(resp.status, payload);
  }
  if (resp.status === 204) return undefined as T;
  const contentType = resp.headers.get('content-type') || '';
  if (contentType.includes('application/json')) return resp.json() as Promise<T>;
  return resp.text() as Promise<T>;
}

export { ApiError };

// === Auth (Toolbox SSO) ===
/** Antwoord van /projects/parse en /projects/parse-bouwflow: het voorstel voor de review. */
export interface ParseResultaat {
  meta: ProjectMeta;
  items: ParsedItem[];
  matching: MatchResult;
  totals: Totals;
  ploeg: { id: string; team: string; firma: string } | null;
  pricelistId: string | null;
  /** Gekozen via het type werken van het project (5.2) of de algemene lijst van de ploeg. */
  pricelistVia?: 'type_werken' | 'ploeg';
  projectType?: string | null;
  /** Voorstel gevelprijslijst (standaard van de onderaannemer, nog niet bevestigd). */
  gevel: GevelConfig | null;
  gevelStandaard?: GevelVariant;
  pdfFilename: string;
  pdfStorageKey: string | null;
  /** null bij een opstartfiche uit Bouwflow (geen AI). */
  usage: AiUsage | null;
  /** Opstartfiche rechtstreeks uit Bouwflow (blok C). */
  bron?: 'bouwflow';
  fiche?: { id: number; naam: string; gewijzigdOp: string | null };
  detected?: {
    ploegFromTeamName: boolean;
    ploegResolvedVia: 'explicit' | 'team-name' | 'bouwflow-sheet' | null;
    extractedTeamNaam: string;
    extractedProjectNummer: string;
    bouwflowProject: { id: number; projectId: string; name: string; budget: number | null; offertewaarde?: number | null } | null;
    bouwflowSheetGroupId: number | null;
    bouwflowSheetGroupName: string | null;
  };
}

export const api = {
  auth: {
    me: () => request<{ user: UserPublic | null }>('/auth/me'),
    /** Wie werkt er achter een gedeeld account (27okt 1). */
    zetPersoon: (persoon: string) => request<{ ok: true; persoon: string | null }>('/auth/persoon', { method: 'POST', body: JSON.stringify({ persoon }) }),
    logout: () => request<{ ok: true; loginUrl: string }>('/auth/logout', { method: 'POST' }),
    /** Server-side redirect naar Toolbox of dev-login. Gebruik via window.location. */
    loginUrl: '/api/auth/login',
  },

  // === Users (admin) ===
  users: {
    list: () => request<{ users: Array<UserPublic & { rolId: string | null; gedeeldAccount: boolean }> }>('/users'),
    create: (body: { email: string; displayName: string; role: 'admin' | 'user'; rolId?: string | null; gedeeldAccount?: boolean }) =>
      request<{ ok: true; id: string }>('/users', { method: 'POST', body: JSON.stringify(body) }),
    /** Rollen met niveau (27okt 7). */
    rollen: () => request<{ rollen: RolRij[] }>('/users/rollen'),
    maakRol: (body: Omit<RolRij, 'id'>) => request<{ ok: true; id: string }>('/users/rollen', { method: 'POST', body: JSON.stringify(body) }),
    wijzigRol: (id: string, body: Partial<Omit<RolRij, 'id'>>) => request<{ ok: true }>(`/users/rollen/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    verwijderRol: (id: string) => request<{ ok: true }>(`/users/rollen/${id}`, { method: 'DELETE' }),
    update: (id: string, body: Partial<{ displayName: string; role: 'admin' | 'user'; rolId: string | null; gedeeldAccount: boolean }>) =>
      request<{ ok: true }>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: string) => request<{ ok: true }>(`/users/${id}`, { method: 'DELETE' }),
    /** Admin: download deploy-snapshot (JSON). Stuur naar `${BASE}/users/snapshot` (sessie-cookie). */
    snapshotUrl: '/api/users/snapshot',
  },

  // === Ploegen ===
  ploegen: {
    list: () => request<{ ploegen: PloegMetPrijslijsten[] }>('/ploegen'),
    create: (body: { team: string; firma: string; email?: string; telefoon?: string; btwNummer?: string; firmaAdres?: string; pricelistId: string; bouwflowId?: number | null; gevelVariant?: GevelVariant; dakHellendPricelistCode?: string | null }) =>
      request<{ ok: true; id: string }>('/ploegen', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<{ team: string; firma: string; email: string; telefoon: string; btwNummer: string; firmaAdres: string; pricelistId: string; bouwflowId: number | null; gevelVariant: GevelVariant; dakHellendPricelistCode: string | null }>) =>
      request<{ ok: true }>(`/ploegen/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: string) => request<{ ok: true }>(`/ploegen/${id}`, { method: 'DELETE' }),
  },

  // === Pricelists ===
  pricelists: {
    list: () => request<{ pricelists: Pricelist[] }>('/pricelists'),
    detail: (id: string) => request<{ pricelist: Pricelist }>(`/pricelists/${id}`),
    create: (body: { name: string; description: string }) =>
      request<{ ok: true; id: string }>('/pricelists', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<{ name: string; description: string }>) =>
      request<{ ok: true }>(`/pricelists/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: string) => request<{ ok: true }>(`/pricelists/${id}`, { method: 'DELETE' }),
    addItem: (pricelistId: string, body: Omit<PricelistItem, 'id' | 'pricelistId'>) =>
      request<{ ok: true; id: string }>(`/pricelists/${pricelistId}/items`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    updateItem: (pricelistId: string, itemId: string, body: Partial<Omit<PricelistItem, 'id' | 'pricelistId'>>) =>
      request<{ ok: true }>(`/pricelists/${pricelistId}/items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    removeItem: (pricelistId: string, itemId: string) =>
      request<{ ok: true }>(`/pricelists/${pricelistId}/items/${itemId}`, { method: 'DELETE' }),
    /** Geheugen van de matcher (geleerde matches + genegeerde regels) voor deze prijslijst. */
    learned: (pricelistId: string) => request<LearningData>(`/pricelists/${pricelistId}/learned`),
    forgetMatch: (pricelistId: string, learnedId: string) =>
      request<{ ok: true }>(`/pricelists/${pricelistId}/learned/${learnedId}`, { method: 'DELETE' }),
    forgetSkip: (pricelistId: string, skipId: string) =>
      request<{ ok: true }>(`/pricelists/${pricelistId}/learned-skips/${skipId}`, { method: 'DELETE' }),
    /** Geleerde match uit-/aanzetten of naar een andere post van dezelfde prijslijst verplaatsen (SPEC_calculatie 10.2–10.3). */
    updateLearned: (pricelistId: string, learnedId: string, body: { actief?: boolean; plItemId?: string }) =>
      request<{ ok: true }>(`/pricelists/${pricelistId}/learned/${learnedId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** Genegeerde regel uit- of aanzetten. */
    updateSkip: (pricelistId: string, skipId: string, body: { actief: boolean }) =>
      request<{ ok: true }>(`/pricelists/${pricelistId}/learned-skips/${skipId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },

  // === Projectniveau: tags, notities, projectlog (27okt 6) ===
  dossier: {
    tags: () => request<{ tags: TagLijstRij[] }>('/dossier/tags'),
    maakVasteTag: (naam: string) => request<{ id: string }>('/dossier/tags', { method: 'POST', body: JSON.stringify({ naam }) }),
    hernoemTag: (id: string, naam: string) => request<{ ok: true }>(`/dossier/tags/${id}`, { method: 'PATCH', body: JSON.stringify({ naam }) }),
    verwijderTagUitLijst: (id: string) => request<{ ok: true }>(`/dossier/tags/${id}`, { method: 'DELETE' }),
    projectTags: (sleutel: string) => request<{ tags: TagRij[] }>(`/dossier/${encodeURIComponent(sleutel)}/tags`),
    voegTagToe: (sleutel: string, body: { naam?: string; tagId?: string }) =>
      request<{ id: string; naam: string }>(`/dossier/${encodeURIComponent(sleutel)}/tags`, { method: 'POST', body: JSON.stringify(body) }),
    verwijderTag: (sleutel: string, tagId: string) => request<{ ok: true }>(`/dossier/${encodeURIComponent(sleutel)}/tags/${tagId}`, { method: 'DELETE' }),
    notities: (sleutel: string) => request<{ notities: NotitieRij[] }>(`/dossier/${encodeURIComponent(sleutel)}/notities`),
    /** `getagd` = user-id's van collega's die een mail krijgen; `calcId` = de mail linkt naar die calculatie. */
    voegNotitieToe: (sleutel: string, tekst: string, extra: { getagd?: string[]; calcId?: string } = {}) =>
      request<{ id: string; gemaild: string[] }>(`/dossier/${encodeURIComponent(sleutel)}/notities`, { method: 'POST', body: JSON.stringify({ tekst, ...extra }) }),
    collegas: () => request<{ collegas: Collega[] }>('/dossier/collegas'),
    wijzigNotitie: (id: string, body: { tekst?: string; vastgepind?: boolean }) =>
      request<{ ok: true }>(`/dossier/notities/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    log: (sleutel: string, f: { categorie?: LogCategorie; userId?: string } = {}) => {
      const q = new URLSearchParams();
      if (f.categorie) q.set('categorie', f.categorie);
      if (f.userId) q.set('userId', f.userId);
      return request<{ entries: ProjectLogRij[]; gebruikers: Array<{ id: string; naam: string }> }>(`/dossier/${encodeURIComponent(sleutel)}/log${q.toString() ? '?' + q : ''}`);
    },
  },

  // === Projects ===
  projects: {
    list: (params?: {
      ploegId?: string;
      status?: ProjectStatus;
      search?: string;
      /** Dedicated zoekopdracht op P-nummer (projectcode, bv. "P-1234"). */
      projectNummer?: string;
      /** Komma-gescheiden type-werken-labels (bv. "Dakwerken,Gevelwerken"). */
      projectTypes?: string;
      year?: number;
      month?: number;
      dateFrom?: string;
      dateTo?: string;
      minTotaal?: number;
      maxTotaal?: number;
      /** Komma-gescheiden tag-id's (27okt 6). */
      tagIds?: string;
      verstuurd?: 'ja' | 'nee';
      oaGefactureerd?: 'ja' | 'nee';
      onvolledig?: 'ja' | 'nee';
      projectleider?: string;
      /** Blok C: opgeleverd (ondertekende bon of betaalde eindfactuur), eindfactuur, 8.2.1. */
      opgeleverd?: 'ja' | 'nee';
      eindfactuur?: 'betaald' | 'verstuurd' | 'geen';
      zonderEindfactuur?: 'ja';
      sortBy?: 'createdAt' | 'totaal' | 'klantNaam' | 'projectNummer';
      sortDir?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
    }) => {
      const q = new URLSearchParams();
      if (params) for (const [k, v] of Object.entries(params)) if (v != null && v !== '') q.set(k, String(v));
      return request<{ projects: ProjectListItem[]; total: number }>(`/projects${q.toString() ? '?' + q.toString() : ''}`);
    },
    /** Distinct type-werken-waarden (met aantallen) voor het dashboard-filter. */
    types: () => request<{ types: Array<{ type: string; count: number }> }>('/projects/types'),
    detail: (id: string) =>
      request<{
        project: {
          id: string; klantNaam: string; klantAdres: string; adviseur: string; projectTitel: string;
          ploegId: string; ploegTeam: string; ploegFirma: string;
          projectNummer: string; opmerkingen: string;
          pricelistName: string; pricelistSnapshotId: string; pdfFilename: string; createdAt: string; updatedAt: string;
          status: ProjectStatus;
          parsedItems: ParsedItem[];
          matchedLines: MatchResult['matchedLines'];
          meerwerkLines: MatchResult['meerwerkLines'];
          skippedItems: MatchResult['skippedItems'];
          /** @deprecated afgeleid uit extraRegels */
          chargeLines: ChargeLine[];
          extraRegels: ExtraRegel[];
          gevel: GevelConfig | null;
          /** Standaard gevelvariant van de onderaannemer (beheer). */
          gevelStandaard: GevelVariant;
          projectPercentage: ProjectPercentage | null;
          verstuurdOp: string | null;
          subtotaal: number; meerwerk: number; extraWerk: number; aanrekening: number; totaal: number;
          offertewaarde: number | null;
          gefactureerd: number | null;
          projectType: string | null;
          bouwflowProjectId: number | null;
          versie: number;
          verstuurdeVersie: number | null;
          verstuurd: boolean;
          oaGefactureerdOp: string | null;
          oaGefactureerdDoor: string | null;
          prijsafwijkingen: PrijsafwijkingRij[];
          versies: VersieRij[];
          vrijgaves: VrijgaveRij[];
          tags: TagRij[];
          vastgepindeNotities: NotitieRij[];
          projectSleutel: string;
          projectleider: string | null;
          openPosten: OpenPost[];
          nuFactureerbaar: number;
          openVoorOplevering: number;
          /** Oplevering en eindfactuur uit Bouwflow (blok C); null = nog niet opgehaald. */
          workflow: WorkflowStaat | null;
          /** Open meldingen uit de polling voor dit P-nummer. */
          meldingen: WorkflowMelding[];
          /** 8.6: laatste poging om de verstuurde PDF in Bouwflow te plaatsen. */
          bouwflowUploadOp: string | null;
          bouwflowUploadVersie: number | null;
          bouwflowUploadFout: string | null;
        }
      }>(`/projects/${id}`),
    versies: (id: string) => request<{ versies: VersieRij[] }>(`/projects/${id}/versies`),
    versiePdfUrl: (id: string, versie: number) => `/api/projects/${id}/versies/${versie}/pdf`,
    vrijgeven: (id: string, postIds: string[]) =>
      request<{ ok: true; vrijgaveId: string; versie: number; bedrag: number; openVoorOplevering: number }>(`/projects/${id}/vrijgave`, { method: 'POST', body: JSON.stringify({ postIds }) }),
    aanvullingPdfUrl: (id: string, vrijgaveId: string) => `/api/projects/${id}/vrijgaves/${vrijgaveId}/pdf`,
    mailAanvulling: (id: string, vrijgaveId: string) =>
      request<{ ok: true; sentTo: string }>(`/projects/${id}/vrijgaves/${vrijgaveId}/email`, { method: 'POST' }),
    zetOaGefactureerd: (id: string, gefactureerd: boolean) =>
      request<{ ok: true }>(`/projects/${id}/oa-gefactureerd`, { method: 'POST', body: JSON.stringify({ gefactureerd }) }),
    exportPdfUrl: (id: string) => `/api/projects/${id}/export/pdf`,
    exportXlsxUrl: (id: string) => `/api/projects/${id}/export/xlsx`,
    /** URL naar de bewaarde originele opstartfiche (open in nieuw tabblad voor verificatie). */
    sourcePdfUrl: (id: string) => `/api/projects/${id}/pdf`,
    /** URL naar de tijdelijke PDF tijdens parse-flow (vóór save) — gebruikt in stap 3 popup. */
    tempPdfUrl: (key: string) => `/api/projects/parse/temp-pdf/${key}`,
    parse: (pdf: File, ploegId?: string) => {
      const fd = new FormData();
      fd.append('pdf', pdf);
      if (ploegId) fd.append('ploegId', ploegId);
      return request<ParseResultaat>('/projects/parse', {
        method: 'POST',
        body: fd,
      });
    },
    /** Opstartfiche rechtstreeks uit Bouwflow (blok C): zelfde antwoord als parse, zonder PDF of AI. */
    parseBouwflow: (bouwflowProjectId: number, ploegId?: string) =>
      request<ParseResultaat>('/projects/parse-bouwflow', { method: 'POST', body: JSON.stringify({ bouwflowProjectId, ploegId }) }),
    create: (body: CreateProjectRequest) =>
      request<{ ok: true; id: string }>('/projects', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: ProjectUpdateBody) =>
      request<{ ok: true; totals: Totals }>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** Percentage op projectniveau; met `exclude` telt de server die calculatie niet mee (live preview). */
    percentage: (sleutels: string[], exclude?: string) => {
      const q = new URLSearchParams({ sleutels: sleutels.join(',') });
      if (exclude) q.set('exclude', exclude);
      return request<{ percentages: Record<string, ProjectPercentage> }>(`/projects/percentage?${q.toString()}`);
    },
    projectniveau: (sleutel: string) =>
      request<{ project: ProjectPercentage; log: ProjectLogRij[]; tags: TagRij[]; notities: NotitieRij[] }>(`/projects/projectniveau/${encodeURIComponent(sleutel)}`),
    /** Voorstel extra werk uit de opstartfiche van het gelinkte project (3.3). 404 + code 'geen_fiche' als er geen is. */
    bouwflowVoorstel: (projectNummer: string, ploegId: string) =>
      request<{
        bron: { calcId: string; klantNaam: string; pdfFilename: string; createdAt: string };
        prijslijst: string;
        gevelVariant: GevelVariant | null;
        bedrag: number;
        weergave: { werkposten: WeergaveRij[]; meerwerk: WeergaveRij[] };
        nietGekoppeld: string[];
      }>(`/projects/bouwflow-voorstel?projectNummer=${encodeURIComponent(projectNummer)}&ploegId=${encodeURIComponent(ploegId)}`),
    gevelConfig: (variant: GevelVariant, ploegId: string) =>
      request<{ config: GevelConfig; standaardVariant: GevelVariant }>(`/projects/gevel-config?variant=${variant}&ploegId=${encodeURIComponent(ploegId)}`),
    remove: (id: string) => request<{ ok: true }>(`/projects/${id}`, { method: 'DELETE' }),
    email: (id: string, body: { customMessage?: string; ccCurrentUser?: boolean; ccBoekhouding?: boolean }) =>
      request<{ ok: true; messageId: string; sentTo: string; bouwflow?: BouwflowUploadAntwoord }>(`/projects/${id}/email`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    whatsapp: (id: string) =>
      request<{ ok: true; wamid: string; sentTo: string; bouwflow?: BouwflowUploadAntwoord }>(`/projects/${id}/whatsapp`, { method: 'POST' }),
  },

  // === Extra werken + aanrekenbare kosten over alle calculaties (4.3, 3.4) ===
  extraRegels: {
    list: (f: ExtraRegelFilters) => request<{ regels: ExtraRegelOverzichtRij[] }>(`/extra-regels?${new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString()}`),
    csvUrl: (f: ExtraRegelFilters) => `${BASE}/extra-regels/csv?${new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString()}`,
  },

  // === Overview ===
  overview: {
    month: (year: number, month: number) =>
      request<{
        year: number;
        month: number;
        aggregates: Array<{
          ploegId: string;
          team: string;
          firma: string;
          count: number;
          totaal: number;
          gemPct: number | null;
          projects: Array<{
            id: string;
            projectNummer: string;
            klantNaam: string;
            totaal: number;
            offertewaarde: number | null;
            gefactureerd: number | null;
            bouwflowProjectId: number | null;
            pct: number | null;
            pctVoorlopig: boolean;
          }>;
        }>;
        grandTotal: number;
        projectCount: number;
      }>(`/overview/month?year=${year}&month=${month}`),
    aiUsage: () =>
      request<{ today: { count: number; cost: number; tokensIn: number; tokensOut: number }; thisMonth: { count: number; cost: number }; allTime: { count: number; cost: number } }>('/overview/ai-usage'),
    monthCsvUrl: (year: number, month: number) => `${BASE}/overview/month/csv?year=${year}&month=${month}`,
    monthPdfUrl: (year: number, month: number, ploegId?: string) => {
      const q = `year=${year}&month=${month}` + (ploegId ? `&ploegId=${encodeURIComponent(ploegId)}` : '');
      return `${BASE}/overview/month/pdf?${q}`;
    },
    emailMonth: (body: { year: number; month: number; ploegId: string; customMessage?: string; ccCurrentUser?: boolean }) =>
      request<{ ok: true; messageId: string; sentTo: string }>('/overview/month/email', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    whatsappMonth: (body: { year: number; month: number; ploegId: string }) =>
      request<{ ok: true; wamid: string; sentTo: string }>('/overview/month/whatsapp', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  // === Bouwflow integration ===
  bouwflow: {
    status: () => request<{ configured: boolean; apiUrl: string }>('/bouwflow/status'),
    projects: (q?: string) => {
      const qs = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
      return request<{ items: Array<{ id: number; projectId: string; name: string; status: string | null; startDate: string | null }> }>(`/bouwflow/projects${qs}`);
    },
    subcontractorGroups: (q?: string) => {
      const qs = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
      return request<{
        items: Array<{
          id: number; name: string; alias: string | null; subcontractorId: number; subcontractorName: string | null;
          localPloeg: { id: string; team: string; firma: string; bouwflowId: number | null } | null;
        }>;
      }>(`/bouwflow/subcontractor-groups${qs}`);
    },
    resolvePloeg: (groupId: number) =>
      request<{ ploeg: { id: string; team: string; firma: string; pricelistId: string } | null }>(`/bouwflow/resolve-ploeg/${groupId}`),
    autoMapApply: () =>
      request<{
        applied: Array<{ ploegId: string; team: string; bouwflowId: number; bouwflowName: string; score: number }>;
        ambiguous: Array<{ ploegId: string; team: string; reason: string }>;
        totalUnmapped: number;
        emailFilled: number;
        phoneFilled: number;
      }>('/bouwflow/auto-map-apply', { method: 'POST' }),
  },

  // === Activity log ===
  // === Workflow-automatisering (blok C): lijst "Te doen" ===
  workflow: {
    meldingen: (alle = false) =>
      request<{ meldingen: WorkflowMelding[]; info: WorkflowInfo }>(`/workflow/meldingen${alle ? '?alle=1' : ''}`),
    status: () => request<WorkflowInfo & { polling: boolean; pollMinuten: number; autoCalculatie: boolean; bouwflowUpload: boolean }>('/workflow/status'),
    afhandelen: (id: string) => request<{ ok: true }>(`/workflow/meldingen/${id}/afgehandeld`, { method: 'POST' }),
    heropen: (id: string) => request<{ ok: true }>(`/workflow/meldingen/${id}/heropen`, { method: 'POST' }),
    poll: () => request<{ bekeken: number; nieuw: number; fouten: number; afgehandeld: number }>('/workflow/poll', { method: 'POST' }),
    zonderEindfactuur: () =>
      request<{ rijen: Array<{ calcId: string; projectNummer: string; klantNaam: string; bedrag: number; datum: string | null }> }>('/workflow/zonder-eindfactuur'),
  },

  activity: {
    list: (params?: {
      userId?: string;
      action?: string;
      entityType?: string;
      entityId?: string;
      since?: number;
      limit?: number;
      offset?: number;
    }) => {
      const q = new URLSearchParams();
      if (params) for (const [k, v] of Object.entries(params)) if (v != null && v !== '') q.set(k, String(v));
      return request<{ entries: ActivityLogEntry[]; total: number }>(`/activity${q.toString() ? '?' + q.toString() : ''}`);
    },
  },
};
